import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "@/i18n/translations";

const LangContext = createContext(null);
const STORAGE_KEY = "wjs.lang";

function getPath(obj, path) {
  return path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState("en");
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === "en" || stored === "zh") setLangState(stored);
    } catch (e) {
      // localStorage unavailable — noop
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang === "zh" ? "zh-Hans" : "en");
  }, [lang]);

  const setLang = useCallback((next) => {
    if (next !== "en" && next !== "zh") return;
    setTransitioning(true);
    // Small delay lets a fade-out play before content swaps
    window.setTimeout(() => {
      setLangState(next);
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch (e) {
        // ignore
      }
      window.setTimeout(() => setTransitioning(false), 220);
    }, 180);
  }, []);

  const t = useCallback(
    (path, fallback) => {
      const value = getPath(translations[lang], path);
      if (value === undefined) return fallback ?? getPath(translations.en, path) ?? path;
      return value;
    },
    [lang],
  );

  const value = useMemo(
    () => ({ lang, setLang, t, transitioning }),
    [lang, setLang, t, transitioning],
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useT() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useT must be used inside LanguageProvider");
  return ctx;
}
